/**
 * DOM-free selection domain for the Ersatzteilefinder.
 *
 * The finder has three selection axes over the payload (plan §D4):
 *   Modell   — a flattened (manufacturer × model) list,
 *   Baujahr  — the selected model's spectrums (MODELLJAHR/Spektrum axis),
 *   Variante — the selected spectrum's drawings (IPL assembly groups).
 *
 * State is three indices into that structure; every render input and the
 * deep-link URL derive from them. Deep-link params keep the LEGACY names and
 * semantics (`model`, `category` = drawing foreignId, `spectrum` =
 * spectrum foreignId) so pre-redesign links restore the equivalent state
 * (product-attribute-description.ts:88-93/175-206 contract).
 *
 * @see docs/plans/2026-07-09 - interactive-shop-foundation/adrs/adr-finder-json-island.md
 */

import { namesExactly, urlValueOfRow } from '../../lib/utils/url-ids';
import type { CompatibleModel, Drawing, FitsForModelsPayload, Spectrum } from '../data/wire';

/** One row of the flattened model list. */
export interface ModelEntry {
  /** Stable key: index into the flattened list */
  index: number;
  manufacturer: string;
  model: CompatibleModel;
  /** Display label, e.g. "Husqvarna Rasenmäher LC 353V" (dedup across manufacturers) */
  label: string;
}

/** Deep-link parameters (legacy-compatible names). */
export interface FinderUrlParams {
  model: string | null;
  category: string | null;
  spectrum: string | null;
}

/** Lowercase + strip diacritics, for the model search box. */
export function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Flatten manufacturers × models preserving payload order. */
export function flattenModels(payload: FitsForModelsPayload): ModelEntry[] {
  const entries: ModelEntry[] = [];
  for (const group of payload.manufacturers) {
    for (const model of group.models) {
      entries.push({
        index: entries.length,
        manufacturer: group.name,
        model,
        label: `${group.name} ${model.device} ${model.name}`,
      });
    }
  }
  return entries;
}

export class FinderSelection {
  private readonly entries: ModelEntry[];
  private modelIndex = 0;
  private spectrumIndex = 0;
  private drawingIndex = 0;

  constructor(private readonly payload: FitsForModelsPayload) {
    this.entries = flattenModels(payload);
  }

  get han(): string {
    return this.payload.han;
  }

  get models(): ModelEntry[] {
    return this.entries;
  }

  get selectedModel(): ModelEntry {
    return this.entries[this.modelIndex];
  }

  get spectrums(): Spectrum[] {
    return this.selectedModel.model.spectrums;
  }

  get selectedSpectrum(): Spectrum {
    return this.spectrums[this.spectrumIndex];
  }

  get drawings(): Drawing[] {
    return this.selectedSpectrum.drawings;
  }

  get selectedDrawing(): Drawing {
    return this.drawings[this.drawingIndex];
  }

  /**
   * The Baujahr axis is hidden when the only spectrum is the synthetic
   * 'Standard' one (plan §D4 — no MODELLJAHR ancestor in the data).
   */
  get showSpectrumAxis(): boolean {
    const spectrums = this.spectrums;
    return spectrums.length > 1 || spectrums[0].foreignId !== '' || spectrums[0].label !== 'Standard';
  }

  get modelIdx(): number {
    return this.modelIndex;
  }

  get spectrumIdx(): number {
    return this.spectrumIndex;
  }

  get drawingIdx(): number {
    return this.drawingIndex;
  }

  selectModel(index: number): void {
    if (index < 0 || index >= this.entries.length) return;
    const previousDrawingName = this.selectedDrawing?.name;
    this.modelIndex = index;
    this.spectrumIndex = 0;
    // Keep the drawing by name across model switches when possible (e.g. stay
    // on "Mähwerk/Mähdeck" while browsing models), else fall back to the first.
    this.drawingIndex = Math.max(
      0,
      this.drawings.findIndex((d) => d.name === previousDrawingName)
    );
  }

  selectSpectrum(index: number): void {
    if (index < 0 || index >= this.spectrums.length) return;
    const previousDrawingName = this.selectedDrawing?.name;
    this.spectrumIndex = index;
    this.drawingIndex = Math.max(
      0,
      this.drawings.findIndex((d) => d.name === previousDrawingName)
    );
  }

  selectDrawing(index: number): void {
    if (index < 0 || index >= this.drawings.length) return;
    this.drawingIndex = index;
  }

  /** Case/diacritic-insensitive model filter for the search box. */
  filterModels(query: string): ModelEntry[] {
    const needle = normalizeForSearch(query.trim());
    if (!needle) return this.entries;
    return this.entries.filter((entry) => normalizeForSearch(entry.label).includes(needle));
  }

  /**
   * Restore state from deep-link params (legacy semantics, most-specific
   * match wins): a drawing (`category`) pins model+spectrum+drawing; a
   * `spectrum` pins model+spectrum; a `model` name pins the model.
   */
  restoreFromParams(params: FinderUrlParams): void {
    if (params.category) {
      for (const entry of this.entries) {
        for (let s = 0; s < entry.model.spectrums.length; s++) {
          const spectrum = entry.model.spectrums[s];
          if (params.spectrum && !namesExactly(params.spectrum, [spectrum.urlId, spectrum.foreignId])) continue;
          const category = params.category;
          const d = spectrum.drawings.findIndex((dr) => namesExactly(category, [dr.urlId, dr.foreignId]));
          if (d >= 0) {
            this.modelIndex = entry.index;
            this.spectrumIndex = s;
            this.drawingIndex = d;
            return;
          }
        }
      }
    }
    if (params.spectrum) {
      for (const entry of this.entries) {
        const spectrumParam = params.spectrum;
        const s = entry.model.spectrums.findIndex((sp) => namesExactly(spectrumParam, [sp.urlId, sp.foreignId]));
        if (s >= 0) {
          this.modelIndex = entry.index;
          this.spectrumIndex = s;
          this.drawingIndex = 0;
          return;
        }
      }
    }
    if (params.model) {
      const m = this.entries.findIndex((entry) => entry.model.name === params.model);
      if (m >= 0) {
        this.modelIndex = m;
        this.spectrumIndex = 0;
        this.drawingIndex = 0;
      }
    }
  }

  /** Current state as deep-link params (written to the page URL). */
  toParams(): FinderUrlParams {
    return {
      model: this.selectedModel.model.name,
      // URL values: the public id when the row carries one (W0-4-public-id-urls §3.2).
      category: urlValueOfRow(this.selectedDrawing),
      spectrum: this.selectedSpectrum.foreignId ? urlValueOfRow(this.selectedSpectrum) : null,
    };
  }

  /**
   * The "Gerät ansehen" CTA target: the drawing's category page with the
   * legacy deep-link params (`ipl-deeplinking.ts` contract on the category side).
   */
  ctaUrl(): string {
    const drawing = this.selectedDrawing;
    const params = new URLSearchParams();
    params.set('category', urlValueOfRow(drawing));
    if (this.selectedSpectrum.foreignId) params.set('spectrum', urlValueOfRow(this.selectedSpectrum));
    params.set('article', this.payload.han);
    return `/${drawing.categoryUrl}?${params.toString()}`;
  }
}
